import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, where, Timestamp, orderBy } from 'firebase/firestore';
import { Container, Card, Form, Button, Spinner, Alert, Row, Col, Modal, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx'; // ★★★ PapaParseの代わりにxlsxをインポート ★★★

/**
 * 期間を指定して注文データをExcel形式で出力するコンポーネント
 */
function PartNumberRanking() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false); // データ取得・処理中の状態
  const [error, setError] = useState('');

  // ユーザー・会社情報を保持
  const [usersMap, setUsersMap] = useState(new Map());
  const [companiesMap, setCompaniesMap] = useState(new Map());
  const [initialDataLoading, setInitialDataLoading] = useState(true);

  // プレビュー機能のためのState
  const [previewData, setPreviewData] = useState([]);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // ユーザー・会社情報を最初に一度だけ取得
  useEffect(() => {
    document.title = '期間別 注文データ出力 | My Order System';
    
    const fetchInitialData = async () => {
      try {
        const usersQuery = getDocs(collection(db, 'users'));
        const companiesQuery = getDocs(collection(db, 'companies'));
        const [usersSnapshot, companiesSnapshot] = await Promise.all([usersQuery, companiesQuery]);
        
        const usersDataMap = new Map();
        usersSnapshot.forEach(doc => usersDataMap.set(doc.id, doc.data()));
        setUsersMap(usersDataMap);
        
        const companiesDataMap = new Map();
        companiesSnapshot.forEach(doc => companiesDataMap.set(doc.id, doc.data()));
        setCompaniesMap(companiesDataMap);

      } catch (err)
 {
        setError('関連データの読み込みに失敗しました。画面を再読み込みしてください。');
        console.error(err);
      } finally {
        setInitialDataLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  /**
   * 「出力内容を確認」ボタンがクリックされたときの処理
   * データを取得し、プレビューモーダルを表示する
   */
  const handlePreview = async () => {
    if (!startDate || !endDate) {
      setError('開始日と終了日を選択してください。');
      return;
    }
    
    setLoading(true);
    setError('');
    setPreviewData([]);

    try {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);

      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, 
        where('orderedAt', '>=', Timestamp.fromDate(start)),
        where('orderedAt', '<=', Timestamp.fromDate(end)),
        orderBy('orderedAt', 'asc')
      );

      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) {
        alert('指定された期間に該当する注文データはありませんでした。');
        setLoading(false);
        return;
      }

      const excelData = [];
      querySnapshot.forEach(orderDoc => {
        const order = orderDoc.data();
        const orderedByUser = usersMap.get(order.orderedByUid);
        const orderedByCompany = companiesMap.get(orderedByUser?.companyId);
        const orderedAtDate = order.orderedAt.toDate();
        const formattedOrderedAt = `${orderedAtDate.getFullYear()}/${orderedAtDate.getMonth() + 1}/${orderedAtDate.getDate()}`;
        
        order.shippingDestinations.forEach(dest => {
          const destinationName = [dest.shippingCompanyName, dest.shippingBranchName, dest.shippingDepartmentName].filter(Boolean).join(' ');
          
          if (order.products && order.products.length > 0) {
            order.products.forEach((product, index) => {
              excelData.push({
                '発注No.': dest.orderNumber,
                '発注日': formattedOrderedAt,
                '発注者会社名': orderedByCompany?.companyName || '',
                '発注者支店名': orderedByUser?.companyPrefix || '',
                '発注者部署名': orderedByUser?.companySuffix || '',
                '発注者': orderedByUser?.personName || '',
                '送付先名・送付先支店名': destinationName,
                '送付先郵便番号': dest.postalCode || '',
                '送付先住所1': dest.address1 || '',
                '送付先住所2': dest.address2 || '',
                '送付先住所3': dest.address3 || '',
                '送付先電話番号': dest.phoneNumber || '',
                '送り状に記載する事項': order.invoiceDetails || '',
                '行': index + 1,
                '品番': product.partNumber,
                '数量': product.quantity,
                '備考': product.remarks || '',
                '出荷予定日': order.estimatedShippingDate || '',
              });
            });
          }
        });
      });

      setPreviewData(excelData);
      setShowPreviewModal(true);

    } catch (err) {
      console.error(err);
      setError('データの取得中にエラーが発生しました。');
    } finally {
      setLoading(false);
    }
  };

  /**
   * ★★★【修正】プレビューモーダル内のダウンロードボタンがクリックされたときの処理 ★★★
   * データをExcel形式でダウンロードする
   */
  const handleDownload = () => {
    if (previewData.length === 0) {
      alert('出力するデータがありません。');
      return;
    }

    // 1. データからワークシートを作成
    const worksheet = XLSX.utils.json_to_sheet(previewData);

    // 2. 新しいワークブックを作成
    const workbook = XLSX.utils.book_new();

    // 3. ワークブックにワークシートを追加
    XLSX.utils.book_append_sheet(workbook, worksheet, '注文データ');

    // 4. ファイルを生成してダウンロード
    XLSX.writeFile(workbook, `注文データ_${startDate}_${endDate}.xlsx`);

    setShowPreviewModal(false); // ダウンロード後にモーダルを閉じる
  };

  if (initialDataLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ height: '80vh' }}>
        <Spinner animation="border" />
        <p className="ms-3 mb-0">関連データを読み込んでいます...</p>
      </Container>
    );
  }

  return (
    <main>
      <Container className="my-4">
        <Card className="shadow-sm">
          <Card.Header as="h2" className="p-3">期間別 注文データ出力</Card.Header>
          <Card.Body className="p-4">
            {error && <Alert variant="danger">{error}</Alert>}
            {/* ★★★【修正】説明文をExcelに変更 ★★★ */}
            <p className="mb-3 text-muted">指定した期間内のすべての注文情報をExcel形式で出力します。</p>
            <Form>
              <Row className="align-items-end g-3 mb-3">
                <Col md>
                  <Form.Group controlId="startDate">
                    <Form.Label>開始日</Form.Label>
                    <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md>
                  <Form.Group controlId="endDate">
                    <Form.Label>終了日</Form.Label>
                    <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                  </Form.Group>
                </Col>
                <Col md="auto">
                  <Button onClick={handlePreview} disabled={loading}>
                    {loading ? <><Spinner as="span" size="sm" /> 検索中...</> : '出力内容を確認'}
                  </Button>
                </Col>
              </Row>
            </Form>
            <hr className="my-4" />
            <Link to="/admin">管理者メニューに戻る</Link> 
          </Card.Body>
        </Card>
      </Container>

      {/* プレビュー用モーダル */}
      <Modal show={showPreviewModal} onHide={() => setShowPreviewModal(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>出力内容の確認</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p><strong>{previewData.length}</strong> 件のデータが出力されます。（プレビューには一部の項目のみ表示）</p>
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <Table striped bordered hover responsive size="sm">
              <thead>
                <tr>
                  <th>発注No.</th>
                  <th>発注日</th>
                  <th>発注者</th>
                  <th>送付先</th>
                  <th>品番</th>
                  <th>数量</th>
                  <th>備考</th>
                  <th>出荷予定日</th>
                </tr>
              </thead>
              <tbody>
                {previewData.map((row, index) => (
                  <tr key={index}>
                    <td>{row['発注No.']}</td>
                    <td>{row['発注日']}</td>
                    <td>{row['発注者']}</td>
                    <td>{row['送付先名・送付先支店名']}</td>
                    <td>{row['商品リスト(品番)']}</td>
                    <td>{row['商品リスト(数量)']}</td>
                    <td>{row['商品リスト(備考)']}</td>
                    <td>{row['出荷予定日']}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            閉じる
          </Button>
          {/* ★★★【修正】ボタンの文言をExcelに変更 ★★★ */}
          <Button variant="primary" onClick={handleDownload}>
            この内容でExcelをダウンロード
          </Button>
        </Modal.Footer>
      </Modal>
    </main>
  );
}

export default PartNumberRanking;

